/**
 * The CRM's contacts, lists and activity, from the party records the platform
 * holds (`backend/app/api/v1/crm.py`).
 *
 * The CRM screen was built against a hardcoded sample roster and never given a
 * source. With demo data off — which is every real tenant — that roster was
 * empty, and the screen fell through to a much thinner fallback. Everything the
 * screen renders existed as real records all along; it just had no endpoint
 * that spoke its shape.
 *
 * The server hands these back in camelCase already, so nothing here re-cases
 * them. Fields that a record does not carry arrive as null, deliberately: the
 * screen has to be able to tell "not on record" from "recorded as zero".
 *
 * Contact ids say which table they belong to, and every mutation is routed on
 * that: `BRK-` a broker master, `bf-` a broker firm, `spn-` a sponsor master,
 * `dc-` a contact captured off the deals. See `partyTypeFor`.
 */
import api from './api'

export type CrmCompanyType =
  | 'Broker'
  | 'Borrower/Sponsor'
  | 'Investor'
  | 'Law Firm'
  | 'Appraiser'
  | 'Insurance'
  | 'Internal'
  /** A contact the deals demonstrably carry, whose side of the table nothing
   *  on record establishes. Shown as such rather than filed under a tab on a
   *  guess. */
  | 'Unknown'

export const CRM_COMPANY_TYPES: CrmCompanyType[] = [
  'Broker',
  'Borrower/Sponsor',
  'Investor',
  'Law Firm',
  'Appraiser',
  'Insurance',
  'Internal',
  'Unknown',
]

/** A person, or an organisation — a borrowing SPV, a brokerage, a fund. */
export type CrmContactKind = 'person' | 'organisation'

/** One logged interaction, exactly as it was logged. */
export interface CrmCommunication {
  id: string
  type: string
  subject: string | null
  preview: string | null
  timestamp: string | null
  /** Null when nobody recorded which way it went. */
  direction: string | null
  user: string | null
  /** The intake record this message IS, when it came off the deals — what
   *  lets a row in the profile open the message itself. Null on a broker's
   *  hand-logged call or meeting, which is a note about a conversation
   *  rather than the conversation. */
  ingestionId?: string | null
  /** The deal the message became, once promoted. */
  dealId?: string | null
  /** How this contact was on THIS message — sender, copied, named. */
  capacity?: string | null
}

/**
 * What external lookup knows about this party.
 *
 * Always present, even before anything has looked: an empty block says "not
 * looked up yet", where omitting it would suggest the concept does not exist.
 * `sources` carries per-source provenance once the intel service fills it, so a
 * reader can see which answer came from where.
 */
/** Which news search produced an article: the person, or their firm. */
export type CrmNewsLane = 'person' | 'company'

/** One sourced news hit stored on the contact's enrichment record. */
export interface CrmNewsArticle {
  title: string
  url: string
  publisher: string | null
  publishedAt: string | null
  lane: CrmNewsLane
  query: string
}

export interface CrmEnrichment {
  linkedIn: string | null
  website: string | null
  previousCompanies: string[]
  yearsExperience: number | null
  dealVolume: string | null
  specialties: string[]
  education: string | null
  licenses: string[]
  newsArticles: number
  /** Articles from the last successful News lookup. Empty until a run stored
   *  some — the News source on `sources` says whether we looked, were
   *  unconfigured, or found nothing. Never fabricated on the client. */
  news?: CrmNewsArticle[]
  lookedUpAt: string | null
  sources: { name: string; ok: boolean; detail: string | null }[]
  /** Observed values the screen has no dedicated field for — a direct line, a
   *  mobile, an office address read out of a signature block. Surfaced with
   *  their source rather than dropped for want of a slot. */
  extra: { field: string; value: unknown; source: string | null; observedAt: string | null }[]
  /** Fields a person has set by hand. A lookup leaves exactly these alone. */
  pinnedFields?: string[]
}

/** The enrichment fields a person may set or correct by hand. */
export interface CrmEnrichmentPatch {
  yearsExperience?: number | null | ''
  dealVolume?: string
  education?: string
  licenses?: string[]
  previousCompanies?: string[]
  specialties?: string[]
  linkedIn?: string
  website?: string
}

export interface CrmDealHistoryRow {
  dealId: string
  property: string | null
  amount: number | null
  stage: string | null
  date: string | null
  outcome: string | null
  /** How this contact appeared on THIS deal — sender, copied, named as the
   *  sponsor's contact. One person is a broker on one deal and a borrower's
   *  principal on the next, and the row says which. Absent on a stored broker
   *  history, which records no capacity. */
  role?: string | null
  /** The deal's asset type as the pipeline states it ("Multifamily",
   *  "Industrial"; "Other" is the platform's own not-identified value). Null
   *  when the deal is not visible to this tenant. */
  assetType?: string | null
}

/**
 * The numbers, in the slots their labels stated.
 *
 * A signature listing a direct line and a switchboard states two different
 * things, and one flat `phone` forces a choice between them. `primary` is
 * whichever is best to call, so a surface with room for one number shows the
 * right one without having to know the rule.
 */
export interface CrmPhones {
  primary: string | null
  direct: string | null
  mobile: string | null
  office: string | null
}

export interface CrmContactMetrics {
  /** Captured contacts only: how many messages this person was observed on,
   *  and how many of those messages became deals. Counts of what is recorded. */
  appearances?: number
  dealCount?: number
  totalSubmissions?: number
  fundedDeals?: number
  hitRate?: number | null
  totalFundedVolume?: number
  isPreferred?: boolean
  isBlocklisted?: boolean
  stalkingHorseCount?: number
  isRepeatOffender?: boolean
  commonSponsors?: string[]
  declinedDeals?: number
  yearsExperience?: number | null
  netWorth?: number | null
  liquidity?: number | null
  flags?: string[]
  /** Firms only: how many broker contacts the directory holds at this firm. */
  contactCount?: number
}

export interface CrmContact {
  id: string
  firstName: string
  lastName: string
  /** Never blank: the name, else the address, else the company. What a row
   *  is called when the correspondence never stated a name. */
  displayName: string
  kind: CrmContactKind
  email: string | null
  /** Every address this contact is known at, primary first. */
  emails: string[]
  phone: string | null
  phones: CrmPhones
  address: string | null
  city?: string | null
  state?: string | null
  postalCode?: string | null
  title: string | null
  /** Sponsor masters only: the entity's legal form (LLC, LP, individual). */
  entityType?: string | null
  company: string | null
  companyId: string | null
  companyType: CrmCompanyType
  /** In words, which rule settled the company type — so "why is this person
   *  filed as a broker" has an answer a reader can check. Null on a master
   *  record, whose role is the record itself. */
  roleEvidence: string | null
  /** The tenant's own staff, by the configured identity suppression set. */
  isInternal: boolean
  /** An organisation rather than a person. "Harbor Point Owner LLC" is a
   *  borrowing entity; the directory drew it identically to a human because
   *  the column was set and never sent. */
  isEntity: boolean
  /** Fields a person has corrected by hand. Capture leaves exactly these
   *  alone and goes on maintaining the rest. */
  editedFields: string[]
  editedBy: string | null
  /** The capacities this contact has appeared in across the deals. */
  capacities: string[]
  linkedDeals: string[]
  /** The recorded deal rows behind those ids, as far as each record has them. */
  dealHistory: CrmDealHistoryRow[]
  /** Null until a real relationship metric exists — never a placeholder zero. */
  score: number | null
  lastContact: string | null
  firstSeen?: string | null
  source: string
  status: string
  tags: string[]
  /** The two lists behind `tags` on a master — markets and asset classes —
   *  kept apart so an edit to one cannot migrate the other. Empty on a
   *  captured row, whose tags are its capacities. */
  markets?: string[]
  assetClasses?: string[]
  location: string | null
  /** Whose location `location` is: a broker record has no city of its own, so
   *  it carries its firm's, said as such ('firm'); 'lookup' is an external
   *  lookup's city for the person. Absent: the contact's own. */
  locationBasis?: 'firm' | 'contact' | 'lookup' | null
  communications: CrmCommunication[]
  enrichment: CrmEnrichment
  notes: string | null
  metrics: CrmContactMetrics
  needsReview: boolean
  /** Retired from the directory. Only present when retired rows were asked for. */
  retired?: boolean
  /** Firms only: the email domains on record for the firm. */
  emailDomains?: string[]
  /** Captured contacts only: the corporate domain the address belongs to. */
  companyDomain?: string | null
  /** The addresses on this record that the platform's own rule
   *  (`contact_names.is_routing_address`) classes as Exchange routing
   *  identifiers: transport names nobody writes to. Casefolded. `emails` is
   *  not filtered by it; the screen decides how to show the two kinds. */
  routingAddresses?: string[]
  /** Who stated a value and when, per capture column (`email`, `phone`,
   *  `direct_phone`, `title`, `company`, `address`, `name`). Captured
   *  contacts, and values a master adopted from a capture. */
  fieldSources?: Record<string, { source: string | null; observedAt: string | null }>
}

export interface CrmContactsResponse {
  contacts: CrmContact[]
  total: number
  /** Which tables the list is drawn from — so an absent party type explains itself. */
  sourcedFrom: string[]
  /** How many contacts the deals themselves supplied, as against the masters
   *  an operator maintains. */
  capturedFromDeals: number
  /** Whether the lender's own staff can be told apart from outside parties:
   *  how many email domains and people tenant identity suppression lists.
   *  With neither, internal staff and intake mailboxes are filed as
   *  counterparties on every deal. Absent from older servers. */
  identitySuppression?: CrmIdentitySuppressionStatus
}

export interface CrmIdentitySuppressionStatus {
  emailDomains: number
  people: number
  staffConfigured: boolean
}

/** What one backfill sweep read and wrote. */
export interface CrmCaptureSummary {
  /** What was actually read successfully — see recordsFailed/dealsFailed
   *  for what was attempted and could not be. */
  recordsRead: number
  dealsRead?: number
  observations: number
  /** Contacts that gained a deal id on this sweep. */
  contactsLinked: number
  contactsRetired?: number
  contactsOnRecord: number
  /** What the dedup pass that follows every sweep did. */
  contactsMerged?: number
  namesCleaned?: number
  namesCleared?: number
  duplicateCandidates?: number
  /** What the sweep could not do. Present and honest, never omitted to make
   *  a partial run read as a clean one. */
  recordsFailed?: number
  dealsFailed?: number
  dedupFailed?: boolean
}

export interface CrmCompany {
  id: string
  name: string
  companyType: CrmCompanyType
  website: string | null
  phone: string | null
  city: string | null
  state: string | null
  emailDomains: string[]
  markets: string[]
  contactCount: number
  needsReview: boolean
  enrichment: CrmEnrichment
}

export interface CrmEnrichResponse {
  partyType: string
  partyId: string
  /** How many fields this run actually changed — 0 means it learned nothing new. */
  changedFields: number
  lookupCount: number
  enrichment: CrmEnrichment
  observations: { field: string; value: unknown; source: string }[]
  sources: { source: string; ok: boolean; detail: string | null }[]
  /** Answered from the process cache rather than a fresh outbound lookup —
   *  the same party looked up again inside the cache's few-hour window.
   *  lookupCount does not move on a cache hit; this is the only signal. */
  cacheHit?: boolean
}

/** One party's enrichment trail: what was learned, from where, and when. */
export interface CrmEnrichmentHistory {
  partyType: string
  partyId: string
  enrichment: CrmEnrichment
  lookupCount: number
  /** Newest first. */
  observations: {
    field: string
    value: unknown
    source: string | null
    observedAt: string | null
    pinned?: boolean
  }[]
}

/** The tenant-wide state of enrichment, counted — never asserted. */
export interface CrmEnrichmentSummary {
  lookedUp: number
  neverLookedUp: number
  lastRunAt: string | null
  runsLast24h: number
  capturesLast24h: number
  mergesLast24h: number
  needsReview: number
  /** Whether Re-enrich searches news (the client's recorded sign-off). */
  newsSearchApproved?: boolean
}

/** One merge on the log (GET /crm/merges). */
export interface CrmMergeLogEntry {
  id: string
  /** Made by the dedup pass, not by a person. */
  automatic: boolean
  actor: string | null
  createdAt: string | null
  reason: string | null
  summary: string
  source: { id: string; name: string }
  target: { id: string; name: string }
  undone: boolean
  undoneAt: string | null
  undoneBy: string | null
  undoable: boolean
  blockedReason: string | null
}

/** Everyone under one organisation (GET /crm/organizations/members). */
export interface CrmOrganizationMembers {
  organization: { name: string; key: string }
  records: { id: string; name: string | null; companyType: string | null; linkedDeals: string[] }[]
  people: {
    id: string
    name: string | null
    title: string | null
    email: string | null
    phone: string | null
    companyType: string | null
    /** Where the person sits as the CRM records it: the broker record's
     *  office, else their own city and state. Never the firm's address.
     *  Absent from a server that predates it, which reads as not recorded. */
    office?: string | null
    /** Where `office` was read, in words. */
    officeBasis?: string | null
    basis: string
    rolesOnThisDeal: string[]
    deals: { dealId: string; property: string | null; role: string | null; stage: string | null }[]
  }[]
  onThisDeal: {
    id: string
    name: string
    contact: string | null
    roles: string[]
    isEntity: boolean
    contactId: string | null
  }[]
}

export type CrmActivityKind =
  | 'capture'
  | 'hygiene'
  | 'dedupe'
  | 'merge'
  | 'unmerge'
  | 'enrich'
  | 'edit'
  | 'create'
  | 'retire'
  | 'restore'
  | 'list'
  | 'note'

/** One thing that happened to the directory. */
export interface CrmActivity {
  id: string
  kind: CrmActivityKind
  contactId: string | null
  contactName: string | null
  summary: string
  detail: Record<string, unknown>
  actor: string | null
  createdAt: string | null
}

export interface CrmActivityResponse {
  items: CrmActivity[]
  total: number
}

/** One side of a duplicate pair, as stored, so the card can show the diff. */
export interface CrmDuplicateRecord {
  id: string
  name: string | null
  email: string | null
  emails: string[]
  company: string | null
  companyDomain: string | null
  title: string | null
  phone: string | null
  directPhone: string | null
  mobilePhone: string | null
  officePhone: string | null
  address: string | null
  city: string | null
  state: string | null
  linkedin: string | null
  website: string | null
  captureCount: number
  dealCount: number
  firstSeenAt: string | null
  lastSeenAt: string | null
  updatedBy: string | null
}

/** A field the two rows do not agree on. `conflict` false means only one side
 *  holds a value — additive on merge, and still what tells them apart. */
export interface CrmDuplicateDifference {
  field: string
  label: string
  values: [string | null, string | null]
  conflict: boolean
}

/** Two rows the engine thinks may be one person, for a human to settle.
 *  Pairs agreeing on name, company and address are merged by the engine and
 *  never reach here. */
export interface CrmDuplicateCandidate {
  ids: [string, string]
  names: [string, string]
  reason: string
  confidence: 'high' | 'medium'
  records?: [CrmDuplicateRecord, CrmDuplicateRecord]
  differing?: CrmDuplicateDifference[]
}

export interface CrmDedupSummary {
  merged: number
  renamed: number
  cleared: number
  retired?: number
  candidates: number
  details: { kind: string; summary: string; ids?: string[] }[]
}

/**
 * Every field a person may correct on a contact. Absent means "leave it";
 * an empty string clears the value.
 *
 * Not every record type stores every field — a sponsor master has no direct
 * line, a firm has no title — and the server says which keys it could not
 * store in `CrmEditResult.ignored` rather than pretending to have saved them.
 */
export interface CrmContactPatch {
  name?: string
  firstName?: string
  lastName?: string
  company?: string
  title?: string
  companyType?: CrmCompanyType
  notes?: string
  isEntity?: boolean
  isInternal?: boolean
  email?: string
  emails?: string[]
  phone?: string
  directPhone?: string
  mobilePhone?: string
  officePhone?: string
  address?: string
  city?: string
  state?: string
  postalCode?: string
  linkedIn?: string
  website?: string
  status?: string
  tags?: string[]
  assetClasses?: string[]
  isPreferred?: boolean
  isBlocklisted?: boolean
  entityType?: string
  yearsExperience?: number | null | ''
  netWorth?: number | null | ''
  liquidity?: number | null | ''
  flags?: string[]
  emailDomains?: string[]
  companyDomain?: string
  roleEvidence?: string
  enrichment?: CrmEnrichmentPatch
}

export interface CrmEditResult {
  id: string
  status: string
  /** The keys the record actually stored. */
  applied: string[]
  /** Keys sent that this record type has nowhere to keep. */
  ignored: string[]
}

export interface CrmCreateContactInput {
  name: string
  companyType: CrmCompanyType
  company?: string
  email?: string
  phone?: string
  title?: string
  notes?: string
  isEntity?: boolean
}

/**
 * Which store a contact id belongs to, so a lookup targets the right record.
 *
 * Read off the id, because the company type cannot answer this: a contact
 * captured off a deal reports 'Broker' when a firm's own email domain settles
 * it, but its id belongs to the capture table, not to `brokers`.
 */
export function partyTypeFor(
  contact: string | Pick<CrmContact, 'id'>,
): 'broker' | 'broker_firm' | 'sponsor' | 'deal_contact' {
  const id = typeof contact === 'string' ? contact : contact.id
  if (id.toUpperCase().startsWith('BRK-')) return 'broker'
  if (id.startsWith('bf-')) return 'broker_firm'
  if (id.startsWith('spn-')) return 'sponsor'
  return 'deal_contact'
}

/** Which record type an id names, in the words the screen uses. */
export function recordKindLabel(id: string): string {
  switch (partyTypeFor(id)) {
    case 'broker':
      return 'broker master'
    case 'broker_firm':
      return 'firm master'
    case 'sponsor':
      return 'sponsor master'
    default:
      return 'captured contact'
  }
}

export const crmService = {
  getContacts: (options?: {
    companyType?: CrmCompanyType
    includeRetired?: boolean
  }): Promise<CrmContactsResponse> =>
    api
      .get('/crm/contacts', {
        params: {
          ...(options?.companyType ? { company_type: options.companyType } : {}),
          ...(options?.includeRetired ? { include_retired: true } : {}),
        },
      })
      .then((r) => r.data as CrmContactsResponse),

  /** Add a contact by hand, of any type.
   *
   *  A broker becomes a broker master (with its firm) and a sponsor a sponsor
   *  master — both MASTER records, which is right for a contact an operator
   *  entered deliberately. Every other type becomes a directory row the
   *  operator owns. The server checks for an existing contact at the same
   *  address or name first and answers 409 with `existingId` rather than
   *  filing a duplicate. */
  createContact: (input: CrmCreateContactInput): Promise<{ id: string }> =>
    api.post('/crm/contacts', input).then((r) => r.data as { id: string }),

  /** Correct one contact, whichever store it lives in.
   *
   *  Send only what changed — this is a correction, not a replacement, and a
   *  form that submits every field would blank whatever it did not show and
   *  pin every one of them against capture. */
  updateContact: (id: string, patch: CrmContactPatch): Promise<CrmEditResult> =>
    api.patch(`/crm/contacts/${id}`, patch).then((r) => r.data as CrmEditResult),

  /** Retire a contact. Archived, never deleted: the row carries the deals it
   *  was seen on, and archiving is what stops capture writing it back. */
  archiveContact: (id: string): Promise<{ id: string; status: string }> =>
    api.delete(`/crm/contacts/${id}`).then((r) => r.data),

  /** Bring a retired contact back. */
  restoreContact: (id: string): Promise<{ id: string; status: string }> =>
    api.post(`/crm/contacts/${id}/restore`).then((r) => r.data),

  /** Fold a duplicate captured contact into the one being kept. */
  mergeContact: (id: string, into: string): Promise<{ id: string; status: string; archived: string }> =>
    api.post(`/crm/contacts/${id}/merge`, { into }).then((r) => r.data),

  /** A person has looked at an automatically captured row and stands by it. */
  confirmContact: (id: string): Promise<{ id: string; status: string }> =>
    api.post(`/crm/contacts/${id}/confirm`).then((r) => r.data),

  /** Add a dated, attributed line to a contact's notes. */
  addNote: (id: string, text: string): Promise<{ id: string; notes: string | null }> =>
    api.post(`/crm/contacts/${id}/notes`, { text }).then((r) => r.data),

  /** Read the deals already on the books into the contact directory.
   *
   *  Capture is automatic on every message and every deal that arrives; this
   *  reads the correspondence that arrived before it did, and runs the dedup
   *  pass over the whole directory afterwards. Idempotent. */
  captureFromDeals: (): Promise<CrmCaptureSummary> =>
    api.post('/crm/contacts/capture').then((r) => r.data as CrmCaptureSummary),

  /** Run the dedup pass over the whole directory now. It also runs on its
   *  own after every capture; this is for a person who wants to see it. */
  dedupe: (): Promise<CrmDedupSummary> =>
    api.post('/crm/contacts/dedupe').then((r) => r.data as CrmDedupSummary),

  /** The merge log: every merge the dedup pass or a person made. */
  getMerges: (limit = 50): Promise<{ items: CrmMergeLogEntry[]; total: number }> =>
    api.get('/crm/merges', { params: { limit } }).then((r) => r.data),

  /** Undo one merge in one click. */
  undoMerge: (id: string): Promise<{ status: string; sourceId: string; targetId: string; kept: string[] }> =>
    api.post(`/crm/merges/${encodeURIComponent(id)}/undo`).then((r) => r.data),

  /** Everyone under one organisation, on a deal and in the CRM. */
  organizationMembers: (name: string, loanId?: string | null): Promise<CrmOrganizationMembers> =>
    api
      .get('/crm/organizations/members', { params: { name, ...(loanId ? { loan_id: loanId } : {}) } })
      .then((r) => r.data as CrmOrganizationMembers),

  /** Pairs the engine could not settle on its own. */
  getDuplicates: (): Promise<{ candidates: CrmDuplicateCandidate[] }> =>
    api.get('/crm/contacts/duplicates').then((r) => r.data),

  /** "Not the same": remembered on the server, so the pair is never offered
   *  again and the automatic pass never folds it. */
  markNotSame: (ids: [string, string]): Promise<{ ids: string[]; status: string; rememberedOn: string[] }> =>
    api.post('/crm/contacts/duplicates/distinct', { ids }).then((r) => r.data),

  getCompanies: (): Promise<{ companies: CrmCompany[]; total: number }> =>
    api.get('/crm/companies').then((r) => r.data),

  /** Run an external lookup and fold what it finds into the stored record.
   *
   *  Triggered by a person, not on page load: one outbound request per
   *  counterparty per render is both rude to the sites involved and impossible
   *  to reason about when an answer looks wrong. */
  enrichParty: (partyType: string, partyId: string): Promise<CrmEnrichResponse> =>
    api.post(`/crm/parties/${partyType}/${partyId}/enrich`).then((r) => r.data),

  /** The same lookup, addressed by contact id — the server works out the store. */
  enrichContact: (id: string): Promise<CrmEnrichResponse> =>
    api.post(`/crm/contacts/${id}/enrich`).then((r) => r.data),

  /** What is already known about a party, and how it came to be known. */
  getEnrichmentHistory: (partyType: string, partyId: string): Promise<CrmEnrichmentHistory> =>
    api.get(`/crm/parties/${partyType}/${partyId}/enrichment`).then((r) => r.data),

  getEnrichmentSummary: (): Promise<CrmEnrichmentSummary> =>
    api.get('/crm/enrichment/summary').then((r) => r.data),

  /** What has been happening to the directory, newest first. */
  getActivity: (options?: {
    limit?: number
    kind?: CrmActivityKind
    contactId?: string
    before?: string
  }): Promise<CrmActivityResponse> =>
    api
      .get('/crm/activity', {
        params: {
          ...(options?.limit ? { limit: options.limit } : {}),
          ...(options?.kind ? { kind: options.kind } : {}),
          ...(options?.contactId ? { contact_id: options.contactId } : {}),
          ...(options?.before ? { before: options.before } : {}),
        },
      })
      .then((r) => r.data as CrmActivityResponse),

  getContactActivity: (id: string, limit = 50): Promise<CrmActivityResponse> =>
    api
      .get(`/crm/contacts/${id}/activity`, { params: { limit } })
      .then((r) => r.data as CrmActivityResponse),
}

// ── Contact lists ───────────────────────────────────────────────────────────
//
// A list is a RULE, not a stored set of ids. "Internal" is "email domain is
// antarcticacapital.com", so the person who joins next week is on it the
// moment their first message is read, without anyone remembering to add them.
// The categories — People, Companies, Brokers, Borrowers, Investors — are
// lists too, seeded by the platform, so one row of chips is one kind of thing.

export interface CrmListRule {
  field: string
  operator: string
  value: string
}

export interface CrmContactList {
  id: string
  name: string
  description: string | null
  /** Whether every rule must hold, or any one of them. */
  match: 'all' | 'any'
  rules: CrmListRule[]
  /** On the list whatever the rules say. */
  pinnedIds: string[]
  /** Off it whatever the rules say. */
  excludedIds: string[]
  /** Seeded by the platform. Its rules are editable; the list cannot be
   *  deleted or renamed — its name is the category vocabulary. */
  isSystem: boolean
  systemKey: string | null
  colour: string | null
  /** Recomputed on every read — a stored count is stale by the next deal. */
  memberCount: number
  /** The rules as a sentence, so a reader sees what the list selects without
   *  opening an editor. */
  summary: string
  /** Display order: system lists first, in the platform's order. */
  position: number
  updatedBy: string | null
  updatedAt: string | null
}

export interface CrmListVocabulary {
  fields: { value: string; label: string }[]
  operators: { value: string; label: string; needsValue: boolean }[]
}

export interface CrmListPreview {
  total: number
  outOf: number
  sample: {
    id: string
    name: string
    email: string | null
    company: string | null
    companyType: string
  }[]
}

export type CrmListInput = {
  name?: string
  description?: string
  match?: 'all' | 'any'
  rules?: CrmListRule[]
  pinnedIds?: string[]
  excludedIds?: string[]
  colour?: string
}

export const crmListService = {
  list: (): Promise<{ lists: CrmContactList[] } & CrmListVocabulary> =>
    api.get('/crm/lists').then((r) => r.data),

  create: (body: CrmListInput): Promise<CrmContactList> =>
    api.post('/crm/lists', body).then((r) => r.data),

  update: (id: string, body: CrmListInput): Promise<CrmContactList> =>
    api.patch(`/crm/lists/${id}`, body).then((r) => r.data),

  remove: (id: string): Promise<{ id: string; status: string }> =>
    api.delete(`/crm/lists/${id}`).then((r) => r.data),

  members: (id: string): Promise<{
    id: string
    name: string | null
    total: number
    contacts: CrmContact[]
    missing?: boolean
  }> => api.get(`/crm/lists/${id}/members`).then((r) => r.data),

  /** Put a contact on a list, or take one off, whatever the rules say. */
  setMembers: (
    id: string,
    body: { add?: string[]; remove?: string[] },
  ): Promise<CrmContactList> => api.post(`/crm/lists/${id}/members`, body).then((r) => r.data),

  /** Who a rule set would select, without saving it. A builder that cannot
   *  show its own answer is one people guess at. */
  preview: (body: { match: 'all' | 'any'; rules: CrmListRule[] }): Promise<CrmListPreview> =>
    api.post('/crm/lists/preview', body).then((r) => r.data),
}

/** Display name for a contact, tolerating an entity that has no surname and
 *  a captured row the correspondence never named. */
export function crmContactName(
  contact: Pick<CrmContact, 'firstName' | 'lastName'> & Partial<Pick<CrmContact, 'displayName' | 'email' | 'company'>>,
): string {
  const joined = [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim()
  return joined || contact.displayName || contact.email || contact.company || '(no name)'
}

/** The message a failed call carried, or the fallback. */
export function crmErrorDetail(failure: unknown, fallback: string): string {
  const detail = (failure as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detail === 'string' ? detail : fallback
}

/** HTTP status on a failed call, if the client attached one. */
export function crmErrorStatus(failure: unknown): number | null {
  const status = (failure as { response?: { status?: number } })?.response?.status
  return typeof status === 'number' ? status : null
}

export function isCrmNotFound(failure: unknown): boolean {
  return crmErrorStatus(failure) === 404
}
